import { useCallback, useState } from 'react';
import { useApiClient } from '../../../utils/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import type {
  AbsencesThisWeekResponse,
  AwayScope,
  TeamAbsenceThisWeekItem,
} from '../types';

export interface UseAwayScopeResult {
  /** The rows to render — the toggled scope's, or the summary's. */
  items: TeamAbsenceThisWeekItem[] | undefined;
  scope: AwayScope;
  switching: boolean;
  error: string | null;
  setScope: (next: AwayScope) => void;
}

/**
 * The "Who's away this week" scope toggle.
 *
 * Mirrors {@link useTeamStatusScope}: it reads its own endpoint rather than
 * re-fetching the dashboard summary. The summary is served through a URL-keyed
 * deduping cache with no notion of query parameters, so reloading it to flip one
 * control would make a cheap toggle feel expensive — and would blank the widgets
 * beside it (AC-39).
 *
 * The summary's payload is still the first paint: `override` starts null, so the
 * caller's `fromSummary` rows and `summaryScope` are shown until somebody
 * actually switches.
 *
 * **Where it differs from the team-status hook: the 204.** There, a 204 means
 * "you may not see that scope", so it surfaces as an error and the previous
 * counts stay. Here both scopes are readable by every authenticated user —
 * absences are org-readable, so the toggle is a *relevance* control, not an
 * authorisation one. A 204 on `scope=mine` means the caller resolves to nobody
 * at all (no linked employee record, no reports, no project peers), which is an
 * empty list at that scope, not a refusal. It renders the scoped empty state
 * (AC-40) rather than an error the reader can do nothing about.
 */
export function useAwayScope(
  fromSummary: TeamAbsenceThisWeekItem[] | undefined,
  summaryScope: AwayScope | undefined,
): UseAwayScopeResult {
  const { getAccessToken } = useAuth();
  const apiClient = useApiClient(getAccessToken);
  const [override, setOverride] = useState<AbsencesThisWeekResponse | null>(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = override?.items ?? fromSummary;
  const scope = override?.scope ?? summaryScope ?? 'mine';

  const setScope = useCallback(
    (next: AwayScope) => {
      if (next === (override?.scope ?? summaryScope ?? 'mine')) return;
      setSwitching(true);
      setError(null);
      void apiClient
        .get<AbsencesThisWeekResponse | null>(
          `/api/dashboard/absences-this-week?scope=${next}`,
        )
        .then((result) => {
          // A 204 arrives as null — see the 204 note on this hook.
          setOverride(result ?? { scope: next, items: [] });
        })
        .catch(() => setError('Could not switch the view. Try again.'))
        .finally(() => setSwitching(false));
    },
    [apiClient, override?.scope, summaryScope],
  );

  return { items, scope, switching, error, setScope };
}
